import { createContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { CableScheduleEntry } from '../utils/cableSchedule';

export interface Project {
  id: string;
  name: string;
  description?: string;
  client?: string;
  preparedBy?: string;
  createdAt: number;
  updatedAt: number;
  cableSchedule: CableScheduleEntry[];
}

interface ProjectContextType {
  currentProject: Project | null;
  projects: Project[];
  createProject: (name: string, description?: string) => Project;
  openProject: (id: string) => void;
  closeProject: () => void;
  updateProject: (updates: Partial<Omit<Project, 'id' | 'createdAt'>>) => void;
  deleteProject: (id: string) => void;
  addToSchedule: (entry: CableScheduleEntry) => void;
  removeFromSchedule: (entryId: string) => void;
  updateScheduleEntry: (entryId: string, updates: Partial<CableScheduleEntry>) => void;
  clearSchedule: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const PROJECTS_STORAGE_KEY = 'bgd-electrical-calc-projects';
const CURRENT_PROJECT_KEY = 'bgd-electrical-calc-current-project';

function generateId(): string {
  return `proj_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>(() => {
    try {
      const stored = localStorage.getItem(PROJECTS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [currentProject, setCurrentProject] = useState<Project | null>(() => {
    try {
      const currentId = localStorage.getItem(CURRENT_PROJECT_KEY);
      if (currentId) {
        const stored = localStorage.getItem(PROJECTS_STORAGE_KEY);
        const projs = stored ? JSON.parse(stored) : [];
        return projs.find((p: Project) => p.id === currentId) || null;
      }
      return null;
    } catch {
      return null;
    }
  });

  // Persist projects to localStorage
  useEffect(() => {
    localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(projects));
  }, [projects]);

  // Persist current project ID
  useEffect(() => {
    if (currentProject) {
      localStorage.setItem(CURRENT_PROJECT_KEY, currentProject.id);
    } else {
      localStorage.removeItem(CURRENT_PROJECT_KEY);
    }
  }, [currentProject]);

  const createProject = useCallback((name: string, description?: string): Project => {
    const now = Date.now();
    const newProject: Project = {
      id: generateId(),
      name,
      description,
      createdAt: now,
      updatedAt: now,
      cableSchedule: [],
    };

    setProjects(prev => [...prev, newProject]);
    setCurrentProject(newProject);
    return newProject;
  }, []);

  const openProject = useCallback((id: string) => {
    const project = projects.find(p => p.id === id);
    if (project) {
      setCurrentProject(project);
    }
  }, [projects]);

  const closeProject = useCallback(() => {
    setCurrentProject(null);
  }, []);

  const updateProject = useCallback((updates: Partial<Omit<Project, 'id' | 'createdAt'>>) => {
    if (!currentProject) return;

    const updated = {
      ...currentProject,
      ...updates,
      updatedAt: Date.now(),
    };

    setCurrentProject(updated);
    setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
  }, [currentProject]);

  const deleteProject = useCallback((id: string) => {
    setProjects(prev => prev.filter(p => p.id !== id));
    if (currentProject?.id === id) {
      setCurrentProject(null);
    }
  }, [currentProject]);

  const addToSchedule = useCallback((entry: CableScheduleEntry) => {
    if (!currentProject) return;

    const updated = {
      ...currentProject,
      cableSchedule: [...currentProject.cableSchedule, entry],
      updatedAt: Date.now(),
    };

    setCurrentProject(updated);
    setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
  }, [currentProject]);

  const removeFromSchedule = useCallback((entryId: string) => {
    if (!currentProject) return;

    const updated = {
      ...currentProject,
      cableSchedule: currentProject.cableSchedule.filter(e => e.id !== entryId),
      updatedAt: Date.now(),
    };

    setCurrentProject(updated);
    setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
  }, [currentProject]);

  const updateScheduleEntry = useCallback((entryId: string, updates: Partial<CableScheduleEntry>) => {
    if (!currentProject) return;

    const updated = {
      ...currentProject,
      cableSchedule: currentProject.cableSchedule.map(e =>
        e.id === entryId ? { ...e, ...updates } : e
      ),
      updatedAt: Date.now(),
    };

    setCurrentProject(updated);
    setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
  }, [currentProject]);

  const clearSchedule = useCallback(() => {
    if (!currentProject) return;

    const updated = {
      ...currentProject,
      cableSchedule: [],
      updatedAt: Date.now(),
    };

    setCurrentProject(updated);
    setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
  }, [currentProject]);

  return (
    <ProjectContext.Provider
      value={{
        currentProject,
        projects,
        createProject,
        openProject,
        closeProject,
        updateProject,
        deleteProject,
        addToSchedule,
        removeFromSchedule,
        updateScheduleEntry,
        clearSchedule,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export { ProjectContext };
